import React, { useCallback, useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Label,
} from "recharts";
import {
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  IconButton,
  Box,
  Grid2,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  GetBinChartData,
  GetWasteChartData,
} from "../../ApiServices/ApiRequests";

import {
  OverviewChart,
  WasteCollectionChartData,
  WasteMngChartData,
} from "../../Models/Models";
import AppColors from "../../Utils/AppColor";
import {
  useWasteManagement,
  WasteManagementType,
} from "../../ContextAPI/WasteManagementContext";
import WasteCard from "./WasteCard";
import { useTranslation } from "react-i18next";
import { useMasterData } from "../../ContextAPI/MasterContext";

interface WasteData {
  month: string;
  [key: string]: number | string;
}
const WasteCollectionTypeBarsDataset: React.FC = () => {
  const itemsPerPage = 6;

  const { masterState } = useMasterData();

  const { wasteManagementState, dispatchWasteManagement } =
    useWasteManagement();
  const { t } = useTranslation();

  const handleInputChange = useCallback(
    (
      field: keyof WasteManagementType,
      value:
        | string
        | boolean
        | number
        | null
        | OverviewChart
        | WasteMngChartData[]
        | WasteCollectionChartData[]
    ) => {
      dispatchWasteManagement({ type: "SET_FIELD", field, value });
    },
    [dispatchWasteManagement]
  );

  const handleBulkChange = useCallback(
    (value: Partial<WasteManagementType>) => {
      dispatchWasteManagement({ type: "BULK_UPDATE", value });
    },
    [dispatchWasteManagement]
  );

  useEffect(() => {
    const fetchData = async (siteCode: string): Promise<void> => {
      try {
        const result: OverviewChart[] = await GetWasteChartData(
          siteCode,
          wasteManagementState.avgWasteGenStschartView === "MOM"
            ? "monthly"
            : "yearly"
        );

        // Map the result to the format expected by your chart
        const chartData: WasteData[] = result.map((item: any) => {
          // Check if item has yaxes
          if (!item.yaxes) {
            console.warn("No yaxes found for item:", item);
            return { month: item.xaxes }; // Set volume to 0 if no yaxes
          }

          const yaxesData: { [key: string]: number } = {};

          // Populate yaxes data dynamically for the bar chart
          item.yaxes.forEach((y: any) => {
            if (y.name && y.value !== undefined) {
              yaxesData[y.name] = parseFloat(y.value) || 0;
            } else {
              console.warn("Invalid yaxes entry:", y);
            }
          });

          // Extract zaxes (for the line chart) and ensure it is parsed as a number
          const volume: number = parseFloat(item.zaxes) || 0;

          return {
            month: item.xaxes, // For the X-axis
            ...yaxesData, // Spread waste types for the bar chart
          };
        });

        // Update state with the processed chart data
        handleInputChange("avgWasteGenStsChartData", chartData);
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    };

    if (masterState.selectedSiteCode.length > 0) {
      fetchData(masterState.selectedSiteCode);
    }
  }, [
    wasteManagementState.avgWasteGenStschartView,
    masterState.selectedSiteCode,
  ]); // Re-fetch data when the view changes

  const handleViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: "MOM" | "YOY"
  ): void => {
    if (newView !== null) {
      var viewChange = {
        avgWasteGenStschartView: newView,
        avgWasteGenStsChartPage: 0,
      };
      handleBulkChange(viewChange);
    }
  };

  const start: number =
    wasteManagementState.avgWasteGenStschartPage * itemsPerPage;
  const paginatedData: WasteCollectionChartData[] =
    wasteManagementState.avgWasteGenStsChartData.slice(
      start,
      start + itemsPerPage
    );

  const maxValue: number =
    Math.ceil(
      Math.max(
        ...wasteManagementState.avgWasteGenStsChartData.map((item) =>
          Number(item.value)
        )
      ) / 10
    ) * 10;

  const formatYAxisTick = (value: number) => `${value}`;

  interface RenderLabelProps {
    x: number;
    y: number;
    value: number;
  }

  const renderLabel = ({ x, y, value }: RenderLabelProps) => {
    return (
      <text x={x + 20} y={y + 10} fill="#fff" fontSize={12} fontWeight="bold">
        {value}%
      </text>
    );
  };

  const wasteKeys: string[] =
    wasteManagementState.avgWasteGenStsChartData.length > 0
      ? wasteManagementState.avgWasteGenStsChartData
          .flatMap((dataItem) => Object.keys(dataItem)) // Get all keys from each object
          .filter((key) => key !== "month" && key !== "Volume") // Filter out "month" and "Volume"
          .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
      : [];
  const colors: string[] = ["#69bd44", "#2baae1", "#e4202c"];

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 8 }}>
        <Card
          variant="elevation"
          style={{ width: "100%", margin: "20px auto" }}
        >
          <CardHeader
            title={
              <Typography variant="h6" color={AppColors.textGray}>
                {t("global.Trends")}
              </Typography>
            }
            subheader={
              <Typography variant="h6">
                {" "}
                {t("global.AverageWasteTypesTrends")}
              </Typography>
            }
            action={
              <ToggleButtonGroup
                value={wasteManagementState.avgWasteGenStschartView}
                exclusive
                onChange={handleViewChange}
                aria-label="View Toggle"
                color="primary"
              >
                <ToggleButton value="YOY" aria-label="Year-over-Year">
                  {t("global.YOY")}
                </ToggleButton>
                <ToggleButton value="MOM" aria-label="Month-over-Month">
                  {t("global.MOM")}
                </ToggleButton>
              </ToggleButtonGroup>
            }
          />
          <Divider />

          <Box sx={{ mr: 2, mt: 2 }} width={"100%"}>
            {paginatedData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={paginatedData}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis
                    ticks={Array.from(
                      { length: maxValue / 10 + 1 },
                      (_, i) => i * 10
                    )}
                    tickFormatter={formatYAxisTick}
                  >
                    <Label
                      value={t("global.VolumeInKg")}
                      angle={-90}
                      position="insideLeft"
                    />
                  </YAxis>
                  <Tooltip
                    formatter={(value, name) => [`${value} kg`, name]} // Format values with percentage and show field name
                    content={({ payload }) => {
                      if (payload && payload.length) {
                        return (
                          <div
                            style={{
                              backgroundColor: "#fff",
                              border: "1px solid #ccc",
                              padding: "10px",
                            }}
                          >
                            {/* Tooltip heading */}
                            <Typography
                              variant="body1" // Use a suitable variant for the heading
                              style={{
                                margin: 0,
                                color: "#000000",
                              }}
                            >
                              {t("global.WasteData")}
                            </Typography>

                            {/* Loop through payload to display the data */}
                            {payload.map((entry, index) => (
                              <Typography
                                key={`item-${index}`}
                                style={{
                                  margin: 0,
                                  color: entry.color,
                                }}
                                variant="body2" // Use a suitable variant for the body text
                              >
                                {entry.name}: {entry.value} kg
                              </Typography>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Line type="monotone" dataKey="volume" stroke="#ff7300" />

                  {wasteKeys.map((key, index) => (
                    <Bar
                      key={index}
                      dataKey={key}
                      fill={colors[index % colors.length]} // Use the color based on index
                      label={renderLabel}
                      barSize={8}
                      radius={10}
                    />
                  ))}

                  {/* Map over data keys to generate bars dynamically */}
                  {/* <Bar dataKey="wet" fill="#69bd44" label={renderLabel} barSize={8} />
                <Bar dataKey="dry" fill="#2baae1" barSize={8} radius={7} />
                <Bar dataKey="ewaste" fill="#e4202c" barSize={8} /> */}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <Typography
                variant="h6"
                color="textSecondary"
                align="center"
                style={{ padding: "20px" }}
              >
                {t("global.NoDataFound")}
              </Typography>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "10px",
              }}
            >
              <IconButton
                onClick={() => {
                  const newValue = Math.max(
                    wasteManagementState.avgWasteGenStschartPage - 1,
                    0
                  );
                  handleInputChange("avgWasteGenStschartPage", newValue);
                }}
                disabled={wasteManagementState.avgWasteGenStschartPage === 0}
              >
                <ArrowBackIosIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  const currentPage = wasteManagementState.avgFillStsChartPage; // Get the current page
                  const newValue = Math.min(
                    currentPage + 1,
                    Math.ceil(
                      wasteManagementState.avgWasteGenStsChartData.length /
                        itemsPerPage
                    ) - 1
                  );
                  handleInputChange("avgWasteGenStschartPage", newValue);
                }}
                disabled={
                  start + itemsPerPage >=
                  wasteManagementState.avgWasteGenStsChartData.length
                }
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </div>
          </Box>
        </Card>
      </Grid2>
      <Grid2 size={{ xs: 4 }}>
        <WasteCard />
      </Grid2>
    </Grid2>
  );
};

export default WasteCollectionTypeBarsDataset;
